import { useState, useMemo, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { usersApi } from '@/services/api';

interface ApiUser {
  id: number;
  name: string;
  email: string;
  phone: string;
  role: string;
  verified: boolean;
  propertiesCount: number;
  joinedDate: string;
  status: string;
  avatar: string;
}

const emptyForm = { name: '', email: '', phone: '', password: '', role: 'Viewer', status: 'Active' };
const roles = ['Admin', 'Manager', 'Agent', 'Viewer', 'Owner', 'Renter', 'Landowner', 'Tenant'];

export default function UsersPage() {
  const [userList, setUserList] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState<ApiUser | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<ApiUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiUser | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const clearMsg = useCallback(() => {
    const t = setTimeout(() => setActionMsg(null), 3500);
    return () => clearTimeout(t);
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params: Record<string, string | number> = { page: 1, per_page: 999999 };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter.toLowerCase();
      if (statusFilter) params.status = statusFilter;

      const res = await usersApi.list(params);
      setUserList((res.data || []) as ApiUser[]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load users';
      setLoadError(msg);
      setActionMsg({ type: 'error', text: msg });
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filtered = useMemo(() => {
    let result = [...userList];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    }
    if (roleFilter) result = result.filter(u => u.role === roleFilter);
    if (statusFilter) result = result.filter(u => u.status === statusFilter);
    return result;
  }, [search, roleFilter, statusFilter, userList]);

  const openCreate = () => {
    setFormData(emptyForm);
    setEditingUser(null);
    setShowCreate(true);
  };

  const openEdit = (user: ApiUser) => {
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone === '-' ? '' : user.phone,
      password: '',
      role: user.role,
      status: user.status,
    });
    setEditingUser(user);
    setShowCreate(true);
    setSelectedUser(null);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) {
      setActionMsg({ type: 'error', text: 'Name and email are required.' });
      clearMsg();
      return;
    }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        role: formData.role.toLowerCase(),
        status: formData.status,
      };
      if (formData.password) payload.password = formData.password;

      if (editingUser) {
        await usersApi.update(editingUser.id, payload);
        setActionMsg({ type: 'success', text: `User "${formData.name}" updated.` });
      } else {
        payload.password = formData.password || undefined;
        await usersApi.create(payload);
        setActionMsg({ type: 'success', text: `User "${formData.name}" created.` });
      }
      setShowCreate(false);
      setEditingUser(null);
      setFormData(emptyForm);
      await fetchUsers();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Operation failed';
      setActionMsg({ type: 'error', text: msg });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await usersApi.delete(deleteTarget.id);
      setActionMsg({ type: 'success', text: `User "${deleteTarget.name}" deleted.` });
      setDeleteTarget(null);
      setSelectedUser(null);
      await fetchUsers();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      setActionMsg({ type: 'error', text: msg });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  };

  const toggleStatus = async (user: ApiUser) => {
    const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    try {
      await usersApi.update(user.id, { status: newStatus });
      setActionMsg({ type: 'success', text: `User "${user.name}" is now ${newStatus.toLowerCase()}.` });
      await fetchUsers();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Status update failed';
      setActionMsg({ type: 'error', text: msg });
    }
    clearMsg();
  };

  const roleBadge = (role: string) => {
    const map: Record<string, string> = {
      'Admin': 'bg-rose-500/15 text-rose-400 border-rose-500/25',
      'Owner': 'bg-accent-100 text-accent-700 border-accent-500/25',
      'Renter': 'bg-blue-500/15 text-blue-400 border-blue-500/25',
      'Manager': 'bg-teal-500/15 text-teal-400 border-teal-500/25',
      'Agent': 'bg-primary-500/15 text-primary-700 border-primary-500/25',
      'Viewer': 'bg-background-200/50 text-foreground-500 border-background-300/30',
      'Landowner': 'bg-accent-100 text-accent-700 border-accent-500/25',
      'Tenant': 'bg-blue-500/15 text-blue-400 border-blue-500/25',
    };
    return map[role] || 'bg-background-200/50 text-foreground-500 border-background-300/30';
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      'Active': 'bg-primary-500/15 text-primary-700 border-primary-500/25',
      'Inactive': 'bg-background-200/50 text-foreground-500 border-background-300/30',
      'Suspended': 'bg-rose-500/15 text-rose-400 border-rose-500/25',
    };
    return map[status] || map.Active;
  };

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Toast */}
        {actionMsg && (
          <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg transition-all ${
            actionMsg.type === 'success' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
          }`}>
            {actionMsg.text}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">Users</h1>
            <p className="text-xs text-foreground-500">{filtered.length} users found</p>
          </div>
          <div className="flex items-center gap-2">
            {loadError && (
              <button onClick={fetchUsers} className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-rose-500/20 text-xs text-rose-400 hover:bg-rose-500/10 cursor-pointer whitespace-nowrap">
                <i className="ri-refresh-line" /> Retry
              </button>
            )}
            <button onClick={openCreate} disabled={submitting} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-50 cursor-pointer whitespace-nowrap">
              <i className="ri-user-add-line" /> Add User
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
            <input type="text" placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
          </div>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="px-3 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
            <option value="">All Roles</option>
            {roles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
          {loading ? (
            <div className="px-4 py-16 text-center">
              <i className="ri-loader-4-line animate-spin text-2xl text-foreground-500" />
              <p className="text-sm text-foreground-500 mt-2">Loading users...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/60">
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">User</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Role</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Verified</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Properties</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Joined</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Status</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(user => (
                    <tr key={user.id} className="border-b border-background-200/50 hover:bg-background-200/80 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] font-bold text-foreground-950">{user.name.split(' ').map(n => n[0]).join('')}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-950">{user.name}</p>
                            <p className="text-[11px] text-foreground-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${roleBadge(user.role)} whitespace-nowrap`}>{user.role}</span></td>
                      <td className="px-4 py-3">
                        {user.verified ? (
                          <span className="inline-flex items-center gap-1 text-xs text-teal-400"><i className="ri-verified-badge-line" /> Verified</span>
                        ) : (
                          <span className="text-xs text-foreground-500">Unverified</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground-500">{user.propertiesCount}</td>
                      <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{user.joinedDate}</td>
                      <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadge(user.status)} whitespace-nowrap`}>{user.status}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setSelectedUser(user)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer" title="View"><i className="ri-eye-line" /></button>
                          <button onClick={() => openEdit(user)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-accent-700 cursor-pointer" title="Edit"><i className="ri-edit-line" /></button>
                          <button onClick={() => setDeleteTarget(user)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-rose-500/10 text-foreground-500 hover:text-rose-400 cursor-pointer" title="Delete"><i className="ri-delete-bin-line" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && !loading && (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-foreground-500">No users found matching your filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* User Detail Slide-out */}
      {selectedUser && (
        <div className="fixed inset-0 z-[1000] flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedUser(null)} />
          <div className="relative z-10 ml-auto w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-background-200/60 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-heading text-lg font-bold text-foreground-950">User Profile</h2>
              <button onClick={() => setSelectedUser(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-foreground-950">{selectedUser.name.split(' ').map(n => n[0]).join('')}</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground-950">{selectedUser.name}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${roleBadge(selectedUser.role)}`}>{selectedUser.role}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadge(selectedUser.status)}`}>{selectedUser.status}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-background-200/50">
                  <span className="text-xs text-foreground-500">Email</span>
                  <span className="text-sm text-foreground-950">{selectedUser.email}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-background-200/50">
                  <span className="text-xs text-foreground-500">Phone</span>
                  <span className="text-sm text-foreground-950">{selectedUser.phone}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-background-200/50">
                  <span className="text-xs text-foreground-500">Verified</span>
                  <span className={`text-sm ${selectedUser.verified ? 'text-teal-400' : 'text-foreground-500'}`}>{selectedUser.verified ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-background-200/50">
                  <span className="text-xs text-foreground-500">Properties</span>
                  <span className="text-sm text-foreground-950">{selectedUser.propertiesCount}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-background-200/50">
                  <span className="text-xs text-foreground-500">Joined</span>
                  <span className="text-sm text-foreground-950">{selectedUser.joinedDate}</span>
                </div>
                {(selectedUser as Record<string, unknown>).enquiries !== undefined && (
                  <div className="flex items-center justify-between py-2 border-b border-background-200/50">
                    <span className="text-xs text-foreground-500">Enquiries</span>
                    <span className="text-sm text-foreground-950">{(selectedUser as Record<string, unknown>).enquiries as number || 0}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-2">
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Actions</h4>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => openEdit(selectedUser)} className="py-2 rounded-lg border border-background-200/60 text-xs text-foreground-500 hover:border-accent-200 hover:text-foreground-950 cursor-pointer whitespace-nowrap">Edit Profile</button>
                  <button onClick={() => { setSelectedUser(null); setDeleteTarget(selectedUser); }} className="py-2 rounded-lg border border-rose-500/20 text-xs text-rose-400 hover:bg-rose-500/10 cursor-pointer whitespace-nowrap">Delete User</button>
                  <button onClick={() => toggleStatus(selectedUser)} className="py-2 rounded-lg border border-background-200/60 text-xs text-foreground-500 hover:border-accent-200 hover:text-foreground-950 cursor-pointer whitespace-nowrap">
                    {selectedUser.status === 'Active' ? 'Deactivate' : 'Activate'}
                  </button>
                  <button onClick={() => setSelectedUser(null)} className="py-2 rounded-lg border border-background-200/60 text-xs text-foreground-500 hover:border-accent-200 hover:text-foreground-950 cursor-pointer whitespace-nowrap">Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreate(false)} />
          <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-950">{editingUser ? 'Edit User' : 'Add User'}</h2>
              <button onClick={() => setShowCreate(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Full Name *</label>
                <input type="text" placeholder="Enter full name" value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Email *</label>
                <input type="email" placeholder="Enter email address" value={formData.email} onChange={e => setFormData(f => ({ ...f, email: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Phone</label>
                <input type="text" placeholder="Enter phone number" value={formData.phone} onChange={e => setFormData(f => ({ ...f, phone: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">{editingUser ? 'New Password (leave blank to keep)' : 'Password'}</label>
                <input type="password" placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'} value={formData.password} onChange={e => setFormData(f => ({ ...f, password: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Role</label>
                  <select value={formData.role} onChange={e => setFormData(f => ({ ...f, role: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                    {roles.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Status</label>
                  <select value={formData.status} onChange={e => setFormData(f => ({ ...f, status: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>
              </div>
              <button onClick={handleSubmit} disabled={submitting} className="w-full py-3 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {submitting ? (
                  <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" /> {editingUser ? 'Updating...' : 'Creating...'}</span>
                ) : (
                  editingUser ? 'Update User' : 'Create User'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-rose-500/15 flex items-center justify-center mb-3">
                <i className="ri-delete-bin-line text-rose-400 text-xl" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950">Delete User?</h3>
              <p className="text-sm text-foreground-500 mt-1">This will permanently delete <strong className="text-foreground-950">{deleteTarget.name}</strong>. This cannot be undone.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 rounded-xl border border-background-200/60 text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={handleDelete} disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600 disabled:opacity-50 cursor-pointer whitespace-nowrap">Delete</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}